import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { KeyRound, Loader2, UserPlus, Smartphone, LockOpen, Lock } from 'lucide-react';
import { authAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';

const ROLES = [
  { valeur: 'patron', libelle: 'Responsable — accès complet' },
  { valeur: 'chef', libelle: "Chef d'équipe — heures de l'équipe" },
  { valeur: 'salarie', libelle: 'Salarié — ses heures seulement' },
];

const libelleRole = (role) => ROLES.find((r) => r.valeur === role)?.libelle.split('—')[0].trim() || role;

// Qui peut se connecter, avec quel code. Réservé au responsable : l'API refuse
// ces routes à tout autre rôle, l'écran n'est que la façade.
const AccountsDialog = ({ employees = [], isOpen, onOpenChange }) => {
  const { toast } = useToast();
  const [comptes, setComptes] = useState(null);
  const [nouveau, setNouveau] = useState({ displayName: '', role: 'salarie', code: '', employeeId: '' });
  const [codeEnCours, setCodeEnCours] = useState({});   // compteId -> code tapé
  const [travail, setTravail] = useState(false);

  const charger = async () => {
    try {
      setComptes(await authAPI.listAccounts());
    } catch (e) {
      toast({ title: 'Lecture des comptes impossible', variant: 'destructive' });
    }
  };

  useEffect(() => { if (isOpen) charger(); /* eslint-disable-line */ }, [isOpen]);

  const creer = async () => {
    setTravail(true);
    try {
      await authAPI.createAccount({
        displayName: nouveau.displayName.trim(),
        role: nouveau.role,
        code: nouveau.code.trim(),
        employeeId: nouveau.employeeId || null,
      });
      toast({ title: 'Compte créé', description: `${nouveau.displayName} peut se connecter` });
      setNouveau({ displayName: '', role: 'salarie', code: '', employeeId: '' });
      charger();
    } catch (e) {
      toast({ title: 'Création refusée', description: e.response?.data?.detail || '', variant: 'destructive' });
    } finally {
      setTravail(false);
    }
  };

  const changerCode = async (compte) => {
    const code = (codeEnCours[compte.id] || '').trim();
    if (!code) return;
    try {
      const { loggedOutDevices } = await authAPI.updateAccount(compte.id, { code });
      toast({
        title: 'Code changé',
        description: loggedOutDevices
          ? `${loggedOutDevices} appareil(s) déconnecté(s) : à reconnecter avec le nouveau code`
          : 'Aucun appareil à reconnecter',
      });
      setCodeEnCours((c) => ({ ...c, [compte.id]: '' }));
      charger();
    } catch (e) {
      toast({ title: 'Changement refusé', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const estBloque = (compte) => compte.lockedUntil && new Date(compte.lockedUntil) > new Date();

  const deverrouiller = async (compte) => {
    try {
      await authAPI.updateAccount(compte.id, { unlock: true });
      toast({ title: `${compte.displayName} peut réessayer`,
              description: "Le code n'a pas changé : il reste le même." });
      charger();
    } catch (e) {
      toast({ title: 'Déverrouillage refusé', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const basculerActif = async (compte) => {
    try {
      await authAPI.updateAccount(compte.id, { isActive: !compte.isActive });
      charger();
    } catch (e) {
      toast({ title: 'Modification refusée', description: e.response?.data?.detail || '', variant: 'destructive' });
    }
  };

  const creationPossible = nouveau.displayName.trim() && nouveau.code.trim().length >= 4
    && (nouveau.role !== 'salarie' || nouveau.employeeId);

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogTrigger asChild><span /></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-green-600" /> Accès à l'application
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Comptes existants</h3>
            {comptes === null && <Loader2 className="w-5 h-5 animate-spin text-green-600" />}
            <div className="space-y-2">
              {(comptes || []).map((compte) => (
                <div key={compte.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <div className="font-medium text-gray-800">
                        {compte.displayName}
                        {!compte.isActive && <span className="ml-2 text-xs text-red-600">désactivé</span>}
                        {estBloque(compte) && (
                          <span className="ml-2 text-xs text-amber-700 inline-flex items-center gap-1">
                            <Lock className="w-3 h-3" /> bloqué (trop d'essais)
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 flex items-center gap-2">
                        {libelleRole(compte.role)}
                        <span className="flex items-center gap-1">
                          <Smartphone className="w-3 h-3" /> {compte.openSessions} appareil(s)
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="text"
                        inputMode="numeric"
                        placeholder="nouveau code"
                        value={codeEnCours[compte.id] || ''}
                        onChange={(e) => setCodeEnCours((c) => ({ ...c, [compte.id]: e.target.value }))}
                        className="w-36 h-9"
                      />
                      <Button size="sm" variant="outline" onClick={() => changerCode(compte)}
                              disabled={!(codeEnCours[compte.id] || '').trim()}>
                        Changer
                      </Button>
                      {estBloque(compte) && (
                        <Button size="sm" variant="outline" onClick={() => deverrouiller(compte)}
                                className="border-amber-300 text-amber-800">
                          <LockOpen className="w-4 h-4 mr-1" /> Déverrouiller
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" onClick={() => basculerActif(compte)}>
                        {compte.isActive ? 'Désactiver' : 'Réactiver'}
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="border-t pt-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <UserPlus className="w-4 h-4" /> Nouveau compte
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Nom affiché</Label>
                <Input value={nouveau.displayName}
                       onChange={(e) => setNouveau({ ...nouveau, displayName: e.target.value })}
                       placeholder="Prénom" />
              </div>
              <div>
                <Label>Rôle</Label>
                <Select value={nouveau.role} onValueChange={(v) => setNouveau({ ...nouveau, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLES.map((r) => <SelectItem key={r.valeur} value={r.valeur}>{r.libelle}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Salarié relié {nouveau.role === 'salarie' && <span className="text-red-500">*</span>}</Label>
                <Select value={nouveau.employeeId || 'aucun'}
                        onValueChange={(v) => setNouveau({ ...nouveau, employeeId: v === 'aucun' ? '' : v })}>
                  <SelectTrigger><SelectValue placeholder="Aucun" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aucun">Aucun</SelectItem>
                    {employees.map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.fullName || `${e.firstName} ${e.lastName}`}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Code (4 à 8 chiffres)</Label>
                <Input type="text" inputMode="numeric" value={nouveau.code}
                       onChange={(e) => setNouveau({ ...nouveau, code: e.target.value })} />
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Le code n'est jamais réaffiché — personne ne peut le relire, il n'est gardé que
              sous forme d'empreinte. En cas d'oubli, tapez-en un nouveau ci-dessus en face de la
              personne et donnez-le-lui : c'est la seule façon de rouvrir un compte.
            </p>
            <Button onClick={creer} disabled={!creationPossible || travail}
                    className="mt-3 bg-green-600 hover:bg-green-700">
              {travail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <UserPlus className="w-4 h-4 mr-2" />}
              Créer le compte
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AccountsDialog;
